import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import Lottie from "lottie-react";

import Button from "../home/components/Button";
import ZydusImage from "../../assets/images/zydus.png";
import PurpleStarImage from "../../assets/images/purpleStar.svg";
import DDxFeatureImage from "../../assets/images/ddx_feature.webp";
import DDxVideoImage from "../../assets/images/ddx_video_thumbnail.webp";
import TatvaAIChakra from "../../assets/animations/tatva_ai_chakra.json";
import DDxBG from "../../assets/images/ddx-bg.webp";
import { updateMetaData } from "./service";
import { shouldShowSponsorship } from "../../utils/appConfig";
import { ReactComponent as ArrowLeft } from "../../assets/images/arrow-left.svg";
import { getLocalStorage } from "../../utils/localStorage";
import { FROM_TATAV_PRACTICE } from "../../utils/constants";
import { navigateToTatvaPractice } from "../../utils/helper";
import { getB2cIdWithFallbacks } from "../../utils/b2cHelper";
import { trackZymedEvent, useZymedOpenedEvent, ZYMED_EVENTS } from "../../utils/zymedAnalytics";

const DDxWelcomePage = () => {
  const targetRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  useZymedOpenedEvent(ZYMED_EVENTS.DDX_OPENED);
  const servicePayerLogo = localStorage.getItem("service_payer_logo_white");
  const [isScrollEnable, setISScrollEnable] = useState(false);

  const onPressGetStarted = async () => {
    trackZymedEvent(ZYMED_EVENTS.DDX_GET_STARTED);
    const b2cId = await getB2cIdWithFallbacks();
    const res = await updateMetaData({
      b2c: b2cId,
      ddx: true,
    });
    if (res?.status === 200) {
      // Opened already fired on this page; the generate page must not count it twice.
      navigate("/generate-ddx", { state: { fromWelcome: true } });
    }
  };

  useEffect(() => {
    let observer: IntersectionObserver;
    let debounceTimeout: NodeJS.Timeout;

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];
      const shouldEnable = entry.intersectionRatio > 0.4;

      // Debounce the state changes to prevent rapid firing
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        if (shouldEnable !== isScrollEnable) {
          setISScrollEnable(shouldEnable);
        }
      }, 100);
    };

    const initObserver = () => {
      observer = new IntersectionObserver(handleIntersection, {
        threshold: [0.4],
        rootMargin: "0px 0px -50px 0px", // Larger margin to prevent rapid changes
      });

      if (targetRef.current) {
        observer.observe(targetRef.current);
      }
    };

    // Delay the observer initialization
    const initTimeout = setTimeout(initObserver, 1500);

    return () => {
      clearTimeout(initTimeout);
      clearTimeout(debounceTimeout);
      if (observer && targetRef.current) {
        observer.unobserve(targetRef.current);
      }
    };
  }, []);
  const handleHomeNavigation = () => {
    const fromPractice = getLocalStorage(FROM_TATAV_PRACTICE);
    if (fromPractice && fromPractice === "true") {
      navigateToTatvaPractice()
    } else {
      navigate("/")
    }
  }

  return (
    <>
      <div className="sticky top-0 px-4 py-3 bg-white flex flex-row items-center justify-between z-20">
        <div className="flex flex-row items-center gap-1.5 bg-white">
          <button onClick={handleHomeNavigation}>
            <ArrowLeft />
          </button>
          <h3 className="text-gray-800 font-semibold">Generate DDx</h3>
        </div>
      </div>
      <div className="relative font-poppins bg-gradient-to-t to-[#200535] from-[#5E0F9B] h-[100dvh] overflow-scroll no-scrollbar">
        <div className="absolute top-0 left-0 h-full w-full">
          <img src={DDxBG} className="h-full w-full opacity-[18%]" alt="gif" />
        </div>
        <div
          className={classNames(
            "relative flex flex-col gap-2 items-center justify-center transition-all duration-700 ease-in-out overflow-hidden",
            isScrollEnable ? "h-[200px]" : "h-[500px]"
          )}
        >
          <div>
            <Lottie
              data-testid="health-monitor-bhojan-icon"
              animationData={TatvaAIChakra}
              loop
              style={{ width: 82, height: 82 }}
            />
          </div>
          <div className="relative bg-gradient-to-r from-[#9EADFF] to-[#FF8091] bg-clip-text text-transparent text-3xl font-semibold">
            Welcome to DDx
          </div>
          {servicePayerLogo && servicePayerLogo !== "null" && servicePayerLogo.trim() !== "" && 
          <div className="relative flex gap-3">
            <p className="text-sm text-white">Powered by</p>
            <img src={servicePayerLogo} className="h-5" alt="service_payer_logo" />
          </div>
          }
        </div>
        <div
          className={classNames(
            "relative bg-[#EDE3FF] rounded-t-3xl",
            isScrollEnable
              ? "h-[calc(100dvh-200px)] overflow-scroll no-scrollbar"
              : ""
          )}
          ref={targetRef}
        >
          <div className="p-6 relative overflow-hidden">
            <div className="absolute -top-6 -right-9">
              <img src={PurpleStarImage} className="h-auto w-28" alt="start" />
            </div>
            <div className="text-left flex flex-col gap-2 font-poppins mt-2">
              <h1 className=" w-[266px] font-bold text-3xl bg-gradient-to-r from-[#4E49E2] via-[#7C2DEF] to-[#B609FF] bg-clip-text text-transparent">
                Revolutionize Diagnosis with AI
              </h1>
              <div className="text-[#454551] w-[98%]">
                Unlock AI-powered precision to identify differential diagnoses.
                Enter patient data, let the AI analyze, and gain clear insights
                to enhance your clinical decisions.
              </div>
            </div>
          </div>

          <div className="relative pb-12 pl-6 pt-9">
            <img
              src={DDxFeatureImage}
              className="w-full h-auto z-10 relative"
              alt="icon"
            />
            <div className="absolute bottom-2 -left-5 z-0">
              <img src={PurpleStarImage} className="h-auto w-28" alt="icon" />
            </div>
          </div>

          <div className="px-6 ">
            <p className="font-semibold text-lg text-left text-[#1F2933]">
              How it works?
            </p>
            <div className="flex flex-col pt-4">
              <>
                <div className="flex items-start justify-center gap-6 ">
                  <div
                    className="w-[52px] h-[48px] rounded-2xl bg-[#BA7DE9]  text-white font-manrope font-bold text-2xl flex items-center justify-center"
                    style={{
                      boxShadow: `0.2px 3.7px 0px 0px #30295C`,
                    }}
                  >
                    1
                  </div>
                  <p className=" w-[80%] text-left text-[#213053]">
                    Provide patient details such as{" "}
                    <span className="font-semibold"> age</span> and
                    <span className="font-semibold"> gender</span>.
                  </p>
                </div>
                <div className="h-7 border-l-[1px] border-dashed  border-[#8584FF] relative left-[7%] w-4 -top-[5%] my-2"></div>
              </>
              <>
                <div className="flex items-start justify-center gap-6">
                  <div
                    className="w-[52px] h-[48px] rounded-2xl bg-[#BA7DE9]  text-white font-manrope font-bold text-2xl flex items-center justify-center"
                    style={{
                      boxShadow: `0.2px 3.7px 0px 0px #30295C`,
                    }}
                  >
                    2
                  </div>
                  <p className="w-[80%] text-left text-[#213053]">
                    Enter <span className="font-semibold"> symptoms</span>{" "}
                    (mandatory) and any additional relevant patients details
                  </p>
                </div>
                <div className="h-7 border-l-[1px] border-dashed  border-[#8584FF] relative left-[7%] w-4 -translate-y-[20%] "></div>
              </>
              <>
                <div className="flex items-start justify-center gap-6  ">
                  <div
                    className="w-[52px] h-[48px] rounded-2xl bg-[#BA7DE9]  text-white font-manrope font-bold text-2xl flex items-center justify-center"
                    style={{
                      boxShadow: `0.2px 3.7px 0px 0px #30295C`,
                    }}
                  >
                    3
                  </div>
                  <p className=" text-left w-[80%] text-[#213053]">
                    Let the AI generate a comprehensive list of differential
                    diagnoses
                  </p>
                </div>
              </>
            </div>
          </div>

          <div className="p-6 pt-10 pb-32">
            <div className="relative w-full cursor-pointer">
              <video
                className="w-full h-auto rounded-[24px]"
                controls
                poster={DDxVideoImage}
                preload="metadata"
              >
                <source src="https://tatvapracticedoctor.blob.core.windows.net/ddx/DDx%20on%20Medeco%202.mp4?sv=2023-01-03&spr=https&st=2025-08-04T07%3A18%3A40Z&se=2029-08-05T07%3A18%3A00Z&sr=b&sp=r&sig=mcG4vn06yLa69VDF8CKlFCwpL%2B3SJnSjTgbWlrNATE0%3D" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
          <div
            className={classNames(
              "fixed bottom-0 left-0 right-0 w-full p-6 bg-transparent z-30 transition-opacity duration-500 ease-out max-w-[1280px] mx-auto flex justify-center",
              isScrollEnable ? "opacity-100 visible" : "opacity-0 invisible"
            )}
          >
            <Button label="Get Started" onClick={onPressGetStarted} />
          </div>
        </div>
      </div>
    </>
  );
};

export default DDxWelcomePage;
